import React, { useMemo } from 'react';

/**
 * SkillRadar — a multi-axis polygon comparing the athlete against a peer
 * cohort. Each axis is a normalized 0..100 score.
 *
 * Props:
 *   metrics: { bat_speed_kmh, efficiency_pct, balls_per_week,
 *              sessions_per_week, mood_avg, load_balance }
 *   cohort:  same shape, with `quantiles` arrays from cohort_service.
 *   size:    pixel diameter of the chart.
 *
 * We use 6 spokes; missing data becomes the cohort median so the shape
 * remains a hexagon (better than a collapsed polygon).
 */
const AXES = [
  { key: 'bat_speed_kmh',     label: 'Bat speed' },
  { key: 'efficiency_pct',    label: 'Efficiency' },
  { key: 'balls_per_week',    label: 'Volume' },
  { key: 'sessions_per_week', label: 'Frequency' },
  { key: 'mood_avg',          label: 'Mood' },
  { key: 'load_balance',      label: 'Load balance' },
];

export default function SkillRadar({ metrics = {}, cohort = {}, size = 280, theme = 'dark' }) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 36;

  const points = useMemo(() => {
    const you = [];
    const peer = [];
    // `cohort` is null when the athlete's bucket has < 5 peers (or when
    // the request hasn't landed yet). `metrics` can be partially-null as
    // well — both fall through to the normalize() defaults.
    const safeMetrics = metrics || {};
    const safeCohort = cohort && cohort.metrics ? cohort.metrics : null;
    AXES.forEach((axis, i) => {
      const angle = (Math.PI * 2 * i) / AXES.length - Math.PI / 2;
      const yourValue = safeMetrics[axis.key];
      const cohortMetric = safeCohort ? safeCohort[axis.key] : null;
      const yourScore = normalize(axis.key, yourValue, cohortMetric);
      const peerScore = normalize(axis.key, cohortMetric?.quantiles?.p50, cohortMetric);
      you.push(polar(cx, cy, r * yourScore, angle));
      peer.push(polar(cx, cy, r * peerScore, angle));
    });
    return { you, peer };
  }, [metrics, cohort, cx, cy, r]);

  const ringColors = theme === 'dark'
    ? ['rgba(255,255,255,0.06)', 'rgba(255,255,255,0.04)']
    : ['rgba(15,23,42,0.06)', 'rgba(15,23,42,0.04)'];

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <defs>
        <linearGradient id="radar-you" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#00A651" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#00D9FF" stopOpacity="0.55" />
        </linearGradient>
      </defs>

      {/* Concentric rings */}
      {[0.25, 0.5, 0.75, 1].map((f, i) => (
        <polygon
          key={f}
          points={AXES.map((_, j) => {
            const a = (Math.PI * 2 * j) / AXES.length - Math.PI / 2;
            const [x, y] = polar(cx, cy, r * f, a);
            return `${x},${y}`;
          }).join(' ')}
          fill="none"
          stroke={ringColors[i % 2]}
          strokeWidth="1"
        />
      ))}

      {/* Spokes */}
      {AXES.map((_, i) => {
        const a = (Math.PI * 2 * i) / AXES.length - Math.PI / 2;
        const [x, y] = polar(cx, cy, r, a);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="rgba(150,160,180,0.2)" />;
      })}

      {/* Peer cohort area */}
      <polygon
        points={points.peer.map((p) => p.join(',')).join(' ')}
        fill="rgba(148,163,184,0.18)"
        stroke="rgba(148,163,184,0.6)"
        strokeWidth="1.5"
        strokeDasharray="4 4"
      />

      {/* You */}
      <polygon
        points={points.you.map((p) => p.join(',')).join(' ')}
        fill="url(#radar-you)"
        stroke="#00C9A7"
        strokeWidth="2"
      />
      {points.you.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="3.5" fill="#00C9A7" />
      ))}

      {/* Axis labels */}
      {AXES.map((axis, i) => {
        const a = (Math.PI * 2 * i) / AXES.length - Math.PI / 2;
        const [x, y] = polar(cx, cy, r + 22, a);
        return (
          <text
            key={axis.key}
            x={x}
            y={y}
            fontSize="11"
            fontWeight="600"
            textAnchor="middle"
            dominantBaseline="middle"
            fill={theme === 'dark' ? '#cbd5e1' : '#475569'}
          >
            {axis.label}
          </text>
        );
      })}
    </svg>
  );
}

function polar(cx, cy, r, a) {
  return [cx + Math.cos(a) * r, cy + Math.sin(a) * r];
}

/** Normalize a raw value to 0..1 using context-aware caps. */
function normalize(key, value, cohortMetric) {
  if (value == null) return 0.4;
  const caps = {
    bat_speed_kmh:     { max: 120 },
    efficiency_pct:    { max: 100 },
    balls_per_week:    { max: 500 },
    sessions_per_week: { max: 8 },
    mood_avg:          { max: 10 },
    load_balance:      { max: 1.5 },  // closer to 1 is "better"
  }[key] || { max: 100 };

  if (key === 'load_balance') {
    // distance from 1.0 (peer p50 or 1) → invert
    const target = 1.0;
    const dist = Math.min(1, Math.abs(value - target) / 0.7);
    return Math.max(0.05, 1 - dist);
  }

  const top = cohortMetric?.quantiles?.p95 || caps.max;
  return Math.max(0.05, Math.min(1, value / top));
}
